import { useMemo, MouseEvent } from 'react';
import { TableColumn } from '@components/TableNew/TableColumns';
import { components } from '@api/api';
import { getRem, Tooltip } from '@datassential/platform-ui-lib';
import { KeywordCell } from './components';
import GrowthPeriodHeader from './components/GrowthPeriodHeader';
import { getFormattedThousand } from '@core/helpers';
import { TopTrendsDataItem } from '@core/apiHooks/useTopTrendsData';

type FilterWord = components['schemas']['MenuTrends.Application.Contracts.Models.FilterWord'];

export type FlavorDataItem = {
    wordId: number;
    knowIt: string;
    haveTriedIt: string;
    loveOrLikeIt: string;
};

export type TopTrendsTableConfigInput = {
    data: TopTrendsDataItem[];
    flavorData?: FlavorDataItem[];
    hasFlavorData: boolean;
    hasFlavorPermission: boolean;
    selectedWords: string[];
    isSelectable: boolean;
    hasTags: boolean;
    growthPeriod: number;
    years: number[];
    onGrowthPeriodChange: (years: number) => void;
    onWordSelect: (wordName: string) => void;
    onSettingsClick: (e: MouseEvent<HTMLDivElement>, word: FilterWord) => void;
    onDrillClick: (word: FilterWord) => void;
    onMenuExamplesClick: (word: FilterWord) => void;
    isLoadingMore?: boolean;
};

const FIRST_COL_WIDTH = 25;
const FIRST_COL_WIDTH_COLLAPSED = 40;
const getDefaultColumnWidth = (hasFlavorData: boolean) => {
    const firstColWidth = hasFlavorData ? FIRST_COL_WIDTH : FIRST_COL_WIDTH_COLLAPSED;
    const columnCount = hasFlavorData ? 8 : 5;
    return (100 - firstColWidth) / columnCount;
};

function formatGrowthValue(value: string | null | undefined): string {
    if (!value) return '';
    const numValue = parseFloat(value);
    return numValue > 0 ? `+${value}` : value;
}

export function useTopTrendsTableConfig({
    data,
    flavorData,
    hasFlavorData,
    hasFlavorPermission,
    selectedWords,
    isSelectable,
    hasTags,
    growthPeriod,
    years,
    onGrowthPeriodChange,
    onWordSelect,
    onSettingsClick,
    onDrillClick,
    onMenuExamplesClick,
}: TopTrendsTableConfigInput): TableColumn[] {
    const colWidth = getDefaultColumnWidth(hasFlavorData);
    const firstColWidth = hasFlavorData ? FIRST_COL_WIDTH : FIRST_COL_WIDTH_COLLAPSED;

    return useMemo(
        (): TableColumn[] => [
            {
                key: 'keyword',
                head: (
                    <div className="TopTrendsTable__keyword-header">
                        {hasTags && (
                            <div className="tag-label is-legend TopTrendsTable__tag-label">
                                AI-POWERED TAG
                            </div>
                        )}
                    </div>
                ),
                headText: 'WORD',
                Value: ({ index }) => {
                    const item = data[index];
                    if (!item?.word) return null;
                    return (
                        <KeywordCell
                            word={item.word}
                            isSelected={selectedWords.includes(item.word.name ?? '')}
                            isSelectable={isSelectable}
                            onSelect={onWordSelect}
                            onSettingsClick={onSettingsClick}
                            onDrillClick={onDrillClick}
                            onMenuExamplesClick={onMenuExamplesClick}
                        />
                    );
                },
                valueText: (index) => data[index]?.word?.name ?? '',
                style: {
                    cell: {
                        flex: `0 0 ${firstColWidth}%`,
                        paddingLeft: getRem(16),
                    },
                    head: {
                        flex: `0 0 ${firstColWidth}%`,
                        paddingLeft: getRem(16),
                    },
                },
            },
            {
                key: 'penetration',
                sortKey: 'penetrationValue',
                head: (
                    <>
                        <span>Penetration</span>
                        <Tooltip id="tooltip-penetration" place="top">
                            % of restaurants
                        </Tooltip>
                    </>
                ),
                getHtmlAttributes: (index) => (index === undefined ? {
                    'data-tooltip-id': 'tooltip-penetration',
                } : {}),
                headText: 'PENETRATION',
                Value: ({ index }) => <>{data[index]?.penetration}</>,
                valueText: (index) => data[index]?.penetration ?? '',
                style: {
                    cell: {
                        flex: `0 0 ${colWidth}%`,
                        justifyContent: 'center',
                    },
                    head: {
                        flex: `0 0 ${colWidth}%`,
                        justifyContent: 'center',
                    },
                },
            },
            {
                key: 'incidence',
                sortKey: 'incidenceValue',
                head: (
                    <>
                        <span>Incidence</span>
                        <Tooltip id="tooltip-incidence" place="top">
                            % of items
                        </Tooltip>
                    </>
                ),
                getHtmlAttributes: (index) => (index === undefined ? {
                    'data-tooltip-id': 'tooltip-incidence',
                } : {}),
                headText: 'INCIDENCE',
                Value: ({ index }) => <>{data[index]?.incidence}</>,
                valueText: (index) => data[index]?.incidence ?? '',
                style: {
                    cell: {
                        flex: `0 0 ${colWidth}%`,
                        justifyContent: 'center',
                    },
                    head: {
                        flex: `0 0 ${colWidth}%`,
                        justifyContent: 'center',
                    },
                },
            },
            {
                key: 'restaurantCount',
                sortKey: 'restaurantCount',
                head: (
                    <>
                        <span>Penetration CT</span>
                        <Tooltip id="tooltip-penetration-ct" place="top">
                            # of Restaurants
                        </Tooltip>
                    </>
                ),
                getHtmlAttributes: (index) => (index === undefined ? {
                    'data-tooltip-id': 'tooltip-penetration-ct',
                } : {}),
                headText: 'PENETRATION CT',
                Value: ({ index }) => <>{getFormattedThousand(data[index]?.restaurantCount ?? 0)}</>,
                valueText: (index) => getFormattedThousand(data[index]?.restaurantCount ?? 0),
                style: {
                    cell: {
                        flex: `0 0 ${colWidth}%`,
                        justifyContent: 'center',
                    },
                    head: {
                        flex: `0 0 ${colWidth}%`,
                        justifyContent: 'center',
                    },
                },
            },
            {
                key: 'growthPeriod',
                head: (
                    <GrowthPeriodHeader
                        years={years}
                        selectedPeriod={growthPeriod}
                        onPeriodChange={onGrowthPeriodChange}
                    />
                ),
                headText: 'GROWTH PERIOD',
                Value: () => null,
                accentLevel: 3,
                style: {
                    cell: {
                        flex: `0 0 ${colWidth * 2}%`,
                        justifyContent: 'center',
                    },
                    head: {
                        flex: `0 0 ${colWidth * 2}%`,
                        justifyContent: 'center',
                    },
                },
                subColumns: [
                    {
                        key: 'pastGrowth',
                        sortKey: 'growthValue',
                        head: 'Past Growth',
                        headText: 'PAST GROWTH',
                        Value: ({ index }) => <>{formatGrowthValue(data[index]?.growth)}</>,
                        valueText: (index) => data[index]?.growth ?? '',
                        style: {
                            cell: {
                                flex: '1 1 50%',
                                justifyContent: 'center',
                            },
                            head: {
                                flex: '1 1 50%',
                                justifyContent: 'center',
                            },
                        },
                        accentLevel: 1,
                    },
                    {
                        key: 'cagr',
                        sortKey: 'cagrValue',
                        head: (
                            <>
                                <span>CAGR</span>
                                <Tooltip id="tooltip-cagr" place="top">
                                    Compound Annual Growth Rate
                                </Tooltip>
                            </>
                        ),
                        getHtmlAttributes: (index) => (index === undefined ? {
                            'data-tooltip-id': 'tooltip-cagr',
                        } : {}),
                        headText: 'CAGR',
                        Value: ({ index }) => <>{formatGrowthValue(data[index]?.cagr)}</>,
                        valueText: (index) => data[index]?.cagr ?? '',
                        style: {
                            cell: {
                                flex: '1 1 50%',
                                justifyContent: 'center',
                            },
                            head: {
                                flex: '1 1 50%',
                                justifyContent: 'center',
                            },
                        },
                        accentLevel: 1,
                    },
                ],
            },
            ...(hasFlavorData ? [{
                key: 'consumers',
                head: (
                    <>
                        <span style={{ opacity: 0.6 }}>Consumers</span>
                        <Tooltip id="tooltip-consumers" place="top">
                            Consumer perceptions are in reference<br/>
                            to each listed food or flavor <u>in general</u> <br/>
                            (not relative to a specific category)
                        </Tooltip>
                    </>
                ),
                getHtmlAttributes: (index) => (index === undefined ? {
                    'data-tooltip-id': 'tooltip-consumers',
                } : {}),
                headText: 'CONSUMERS',
                Value: () => null,
                accentLevel: 2,
                style: {
                    cell: {
                        flex: `0 0 ${colWidth * 3}%`,
                        justifyContent: 'center',
                    },
                    head: {
                        flex: `0 0 ${colWidth * 3}%`,
                        justifyContent: 'center',
                    },
                },
                subColumns: [
                    {
                        key: 'knowIt',
                        sortKey: hasFlavorPermission ? 'knowItValue' : undefined,
                        head: 'Know It',
                        headText: 'KNOW IT',
                        Value: ({ index }) => {
                            const item = data[index];
                            const flavorItem = flavorData?.find(f => f.wordId === item?.word?.id);
                            return <>{flavorItem?.knowIt}</>;
                        },
                        valueText: (index) => {
                            const item = data[index];
                            const flavorItem = flavorData?.find(f => f.wordId === item?.word?.id);
                            const value = flavorItem?.knowIt;
                            return value === '-' ? '' : (value?.replace('%', '') ?? '');
                        },
                        style: {
                            cell: {
                                flex: '1 1 33.33%',
                                justifyContent: 'center',
                            },
                            head: {
                                flex: '1 1 33.33%',
                                justifyContent: 'center',
                            },
                        },
                        accentLevel: 0,
                    },
                    {
                        key: 'haveTriedIt',
                        sortKey: hasFlavorPermission ? 'haveTriedItValue' : undefined,
                        head: 'Have Tried It',
                        headText: 'HAVE TRIED IT',
                        Value: ({ index }) => {
                            const item = data[index];
                            const flavorItem = flavorData?.find(f => f.wordId === item?.word?.id);
                            return <>{flavorItem?.haveTriedIt}</>;
                        },
                        valueText: (index) => {
                            const item = data[index];
                            const flavorItem = flavorData?.find(f => f.wordId === item?.word?.id);
                            const value = flavorItem?.haveTriedIt;
                            return value === '-' ? '' : (value?.replace('%', '') ?? '');
                        },
                        style: {
                            cell: {
                                flex: '1 1 33.33%',
                                justifyContent: 'center',
                            },
                            head: {
                                flex: '1 1 33.33%',
                                justifyContent: 'center',
                            },
                        },
                        accentLevel: 0,
                    },
                    {
                        key: 'loveOrLikeIt',
                        sortKey: hasFlavorPermission ? 'loveOrLikeItValue' : undefined,
                        head: 'Love or Like It',
                        headText: 'LOVE OR LIKE IT',
                        Value: ({ index }) => {
                            const item = data[index];
                            const flavorItem = flavorData?.find(f => f.wordId === item?.word?.id);
                            return <>{flavorItem?.loveOrLikeIt}</>;
                        },
                        valueText: (index) => {
                            const item = data[index];
                            const flavorItem = flavorData?.find(f => f.wordId === item?.word?.id);
                            const value = flavorItem?.loveOrLikeIt;
                            return value === '-' ? '' : (value?.replace('%', '') ?? '');
                        },
                        style: {
                            cell: {
                                flex: '1 1 33.33%',
                                justifyContent: 'center',
                            },
                            head: {
                                flex: '1 1 33.33%',
                                justifyContent: 'center',
                            },
                        },
                        accentLevel: 0,
                    },
                ],
            } as TableColumn] : []),
        ],
        [
            data,
            flavorData,
            hasFlavorData,
            hasFlavorPermission,
            selectedWords,
            isSelectable,
            hasTags,
            growthPeriod,
            years,
            onGrowthPeriodChange,
            onWordSelect,
            onSettingsClick,
            onDrillClick,
            onMenuExamplesClick,
            colWidth,
            firstColWidth,
        ]
    );
}
